import React, { Component, Fragment, useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Map, MapContext } from "@trackcode/map";
import LayoutContext from "features/app/LayoutContext";
import { Scopes, ScopeDrivers } from "./";
import WencoMapLegend from "./WencoMapLegend";
import WencoCustomerMapPinLayer from "./WencoCustomerMapPinLayer";
import mapActions from "../state/mapActions";

const DRIVER_CENTER_ZOOM = 16;

// Fallback-Zentrum, falls die logisticcompany keine Koordinaten hinterlegt hat.
// Standort der Edgar Rothermel Internationale Spedition GmbH, Industriestraße 2,
// 76684 Östringen. Verhindert, dass die Karte ohne gültiges Center grau bleibt.
const DEFAULT_MAP_CENTER = { lat: 49.212536, lng: 8.691078 };

const isValidCoordinate = value => {
  const num = Number(value);
  return Number.isFinite(num) && num !== 0;
};

function MapDriverCenter({ locations, registerCenterDriverOnMap }) {
  const map = useContext(MapContext);
  const locationsRef = useRef(locations);

  locationsRef.current = locations;

  useEffect(() => {
    if (typeof registerCenterDriverOnMap !== "function" || !map) {
      return undefined;
    }

    registerCenterDriverOnMap(driverId => {
      const currentLocations = locationsRef.current;
      const driverLocation =
        currentLocations[driverId] ||
        currentLocations[String(driverId)] ||
        currentLocations[Number(driverId)];
      const [location] = driverLocation || [];

      if (!location) {
        return;
      }

      const lat = Number(location.latitude);
      const lng = Number(location.longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      map.panTo(new window.google.maps.LatLng(lat, lng));
      map.setZoom(DRIVER_CENTER_ZOOM);
    });

    return () => registerCenterDriverOnMap(null);
  }, [map, registerCenterDriverOnMap]);

  return null;
}

MapDriverCenter.propTypes = {
  locations: PropTypes.shape({}).isRequired,
  registerCenterDriverOnMap: PropTypes.func
};

class MapWrapper extends Component {
  static contextType = LayoutContext;

  static propTypes = {
    /** clear scope options */
    clearOptions: PropTypes.func.isRequired,
    /** list of handle scopes, default all scopes */
    handleScopes: PropTypes.arrayOf(PropTypes.string),
    /** Wenco workbook: show Gebiet legend overlay */
    showWencoMapLegend: PropTypes.bool,
    companyLat: PropTypes.number.isRequired,
    companyLng: PropTypes.number.isRequired,
    logisticCompanyId: PropTypes.number.isRequired,
    locationsLoaded: PropTypes.bool.isRequired,
    locations: PropTypes.shape({}).isRequired,
    managedLogisticCompanies: PropTypes.shape({
      id: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
      })
    }).isRequired
  };

  static defaultProps = {
    handleScopes: [],
    showWencoMapLegend: false
  };

  constructor(props) {
    super(props);
    this.driversBounds = [];
  }

  state = {
    driversBounds: []
  };

  setMapRef = ref => {
    if (!this.mapRef) {
      this.mapRef = ref;
      this.forceUpdate();
    } else {
      this.mapRef = ref;
    }
  };

  setDriversBounds = driversBounds => {
    this.setState({ driversBounds });
  };

  getCompanyLatLng() {
    const {
      companyLat,
      companyLng,
      logisticCompanyId,
      managedLogisticCompanies
    } = this.props;
    const company = managedLogisticCompanies[logisticCompanyId];
    if (
      company &&
      isValidCoordinate(company.latitude) &&
      isValidCoordinate(company.longitude)
    ) {
      return {
        lat: Number(company.latitude),
        lng: Number(company.longitude)
      };
    }
    // coordinates of the default logisticcompany (from the logged-in user)
    if (isValidCoordinate(companyLat) && isValidCoordinate(companyLng)) {
      return {
        lat: Number(companyLat),
        lng: Number(companyLng)
      };
    }
    // Ohne hinterlegte Koordinaten würde Google Maps ein ungültiges Center
    // erhalten und grau bleiben – daher ein definierter Fallback.
    return DEFAULT_MAP_CENTER;
  }

  handleOptions = (scopeNames, scopes) => {
    const { clearOptions, handleScopes } = this.props;
    const { driversBounds } = this.state;

    if (!scopeNames.length) return;

    // only support options for one scope simultaneously (ignore empty option objects)
    const scopesWithOptions = scopeNames.filter(name => {
      const options = scopes[name] && scopes[name].options;
      return options && (options.fitBounds || options.zoom >= 0);
    });
    const [scopeName] = scopesWithOptions.sort((a, b) => {
      if (a === "scheduling") return -1;
      if (b === "scheduling") return 1;
      return 0;
    });

    if (!scopeName) return;
    if (handleScopes.length > 0 && !handleScopes.includes(scopeName)) return;

    const { options } = scopes[scopeName];

    if (options.fitBounds) {
      const bounds = scopeName === "drivers" ? driversBounds : options.bounds;
      if (bounds && bounds.length > 0) {
        this.mapRef.fitBounds(bounds);
      }
    }
    if (options.zoom >= 0) {
      this.mapRef.setZoom(options.zoom);
    }

    clearOptions(scopeName);
  };

  render() {
    const { handleScopes, showWencoMapLegend, locations } = this.props;
    const { registerCenterDriverOnMap } = this.context || {};

    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <Map
          defaultZoom={12}
          defaultCenter={this.getCompanyLatLng()}
          ref={this.setMapRef}
        >
          {this.mapRef && (
            <Fragment>
              <MapDriverCenter
                locations={locations}
                registerCenterDriverOnMap={registerCenterDriverOnMap}
              />
              <ScopeDrivers getBounds={this.setDriversBounds} />
              <Scopes
                handleOptions={this.handleOptions}
                handleScopes={handleScopes}
              />
              <WencoCustomerMapPinLayer />
            </Fragment>
          )}
        </Map>
        <WencoMapLegend visible={showWencoMapLegend} />
      </div>
    );
  }
}

const mapStateToProps = ({
  driver: { locations = {}, locationsLoaded = false },
  auth: {
    user: { companylatitude: companyLat, companylongitude: companyLng },
    logisticCompanyId = 0,
    managedLogisticCompanies = {}
  }
}) => ({
  locations,
  locationsLoaded,
  companyLat,
  companyLng,
  logisticCompanyId,
  managedLogisticCompanies
});

const mapDispatchToProps = {
  clearOptions: mapActions.clearOptions
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MapWrapper);
